<script setup lang="ts">
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { computed, ref, watch } from 'vue'
import { T } from '@tolgee/vue'
import { useConfig } from '@/composables/game/config'
import { DAILY_TASK_ID } from '@/types/shared/player'
import { formatNumberSemiTruncate } from '@/helpers/utils'
import CheckIcon from '@/components/icons/CheckIcon.vue'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import PlusIcon from '@/components/icons/PlusIcon.vue'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { useConfetti } from '@/composables/game/confetti'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import { syncPlayer } from '@/composables/game/player'
import TransactionFailedModal from '@/components/modals/TransactionFailedModal.vue'
import { useToaster } from '@/composables/game/toasters'
import { useAuth } from '@/composables/auth'
import NumberFormatTooltip from '@/components/NumberFormatTooltip.vue'
import { useDailyTx } from '@/composables/game/daily'
import api from '@/services/api'
import { sleep } from '@/services/helper'
import { useNow } from '@/composables/game/time'
import { env } from '@/env'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'

const props = defineProps<{
  show: boolean
  isDone?: boolean
}>()
defineEmits(['hide'])
const { config } = useConfig()
const { open } = useTonConnectModal()
const { rawAddress, doTonPurchaseTransaction } = useWalletConnect()
const { showConfetti } = useConfetti()
const { updateState: updateFortuneWheelState } = useFortuneWheel()
const { notify } = useToaster()
const { addHapticFeedback } = useHapticFeedback()
const { profile } = useAuth()
const { hasRecentlyDoneTx, lastTxTimestamp } = useDailyTx()
const { now } = useNow()

const taskItem = computed(() => {
  const mustItem = config.value.tasks.find(t => t.id === DAILY_TASK_ID.TON_DAILY_CHECKIN)
  if (!mustItem) {
    api.reportError({
      code: 'taskItemNotFound',
      description: `Task item with the id of ${DAILY_TASK_ID.TON_DAILY_CHECKIN} not found in tasks list`,
      metadata: {},
    })
    throw new Error('Task item not found')
  }
  return mustItem
})
const coinBenefit = computed(() => taskItem.value.reward)
const spinBenefit = computed(() => taskItem.value.spins)
const ticketBenefit = computed(() => config.value.game.tasks.dailyTasks[DAILY_TASK_ID.TON_DAILY_CHECKIN].tickets)
const hasFailed = ref(false)
const transactionDone = ref(Boolean(props.isDone))
const transactionIsInProgress = ref(false)
const showTransactionFailedModal = ref(false)
const isButtonLoading = computed(() => transactionIsInProgress.value || hasRecentlyDoneTx.value)

function onConnect() {
  open()
}

function setLastTx() {
  lastTxTimestamp.value = now.value
}

async function onSuccessfulClaim() {
  addHapticFeedback('medium')
  setLastTx()
  transactionIsInProgress.value = false
  showConfetti()
  transactionDone.value = true
  await syncPlayer()
  await updateFortuneWheelState()
}

async function checkForNotificationsCount() {
  for (let i = 0; i < 20; i++) {
    await sleep(3000)
    try {
      const { count } = await api.getNotificationsCount()
      if (count > 0) {
        transactionDone.value = true
        transactionIsInProgress.value = false
        await onSuccessfulClaim()
        return true
      }
    } catch (e) {
      transactionIsInProgress.value = false
      return false
    }
  }
  setLastTx()
  transactionIsInProgress.value = false
  return false
}

async function onTransaction() {
  addHapticFeedback('light')
  transactionIsInProgress.value = true
  try {
    await doTonPurchaseTransaction({
      nonce: `p:3:dailyCheckin:${profile.value.referralCode}`,
      tonAmount: !env.app.isProd ? 0.01 : 0.25,
    })
    await checkForNotificationsCount()
  } catch (e) {
    notify({ type: 'error', title: 'Incomplete. Try again' })
    setLastTx()
    transactionIsInProgress.value = false
  }
}

watch(hasFailed, hasFailed => {
  if (hasFailed) {
    showTransactionFailedModal.value = true
  }
})

watch(() => props.isDone, async (isDone) => {
  if (isDone) {
    transactionDone.value = true
  }
}, { immediate: true })

watch(() => props.show, show => {
  if (show) {
    transactionIsInProgress.value = false
  }
})
</script>

<template>
  <TheBottomSheet
    :show="show"
    outer-content-class="[background-image:url('@/assets/images/gold-bg-ton.png')]"
    content-class="pt-0 [background-image:url('@/assets/images/gold-bg-ton.png')] flex flex-col items-center max-w-full overflow-x-hidden"
    close-button-class="text-black z-[10]"
    @hide="$emit('hide')"
  >
    <div class="mt-[48px] relative size-[88px] min-w-[88px] hidden-with-small-height">
      <img
        class="absolute size-[176px] -left-[120px] -top-[88px] object-cover max-w-fit z-[3]"
        src="@/assets/images/toncoin1.png"
      >
      <img
        class="w-full h-full object-cover z-[2] relative"
        src="@/assets/images/gifs/duck-money-eye.gif"
      >
      <img
        class="absolute size-[176px] object-cover max-w-fit left-[18px] -top-[56px] z-[1]"
        src="@/assets/images/toncoin2.png"
      >
      <img
        class="absolute size-[162px] object-cover max-w-fit z-[1] -top-[130px] left-[74px] -rotate-[12deg]"
        src="@/assets/images/toncoin3.png"
      >
    </div>
    <div
      class="mt-0.5 w-full flex flex-col items-center text-black gap-y-6 extra-top-padding"
    >
      <div class="flex flex-col gap-y-2 items-center">
        <div class="text-center text-[28px] leading-[30px] font-bold -tracking-[0.64px]">
          <T
            v-if="transactionDone"
            key-name="tasks.daily_transaction_modal.title_success"
            default-value="Transaction successful!"
          />
          <T
            v-else
            key-name="tasks.daily_transaction_modal.title"
            default-value="Promote TON Blockchain"
          />
        </div>
        <div class="max-w-[328px] text-base font-semibold text-center">
          <T
            key-name="tasks.daily_transaction_modal.description"
            default-value="Make a small daily TON transaction and receive massive rewards!"
          />
        </div>
      </div>
      <div class="flex flex-col gap-y-1.5 items-center w-max">
        <div
          v-if="coinBenefit"
          class="benefit-box benefit-box-glow"
        >
          <CheckIcon
            v-if="transactionDone"
            class="benefit-box-check scale-in-medium"
          />
          <img
            :src="CoinIcon"
            class="h-[28px] min-auto"
          >
          <NumberFormatTooltip
            :number="coinBenefit"
            :formatting-type="'full'"
          >
            <span
              class="benefit-box-value"
            >{{ formatNumberSemiTruncate(coinBenefit) }}</span>
          </NumberFormatTooltip>
        </div>
        <PlusIcon
          v-if="coinBenefit && (spinBenefit || ticketBenefit)"
          class="text-white size-4"
        />
        <div
          v-if="spinBenefit"
          class="benefit-box benefit-box-glow"
        >
          <CheckIcon
            v-if="transactionDone"
            class="benefit-box-check scale-in-medium"
          />
          <img
            src="@/assets/images/fortune-wheel.webp"
            class="size-[28px] min-w-[28px] object-cover"
          >
          <span class="benefit-box-value">
            <T
              key-name="fortune_wheel.info.more_spins.count_spin"
              default-value="{count, plural, one {# spin} other {# spins}}"
              :params="{count: spinBenefit}"
            />
          </span>
        </div>
        <PlusIcon
          v-if="spinBenefit && ticketBenefit"
          class="text-white size-4"
        />
        <div
          v-if="ticketBenefit"
          class="benefit-box benefit-box-glow"
        >
          <CheckIcon
            v-if="transactionDone"
            class="benefit-box-check scale-in-medium"
          />
          <img
            src="@/assets/images/emojis/emoji-ticket.png"
            class="size-[28px] min-w-[28px] object-cover"
          >
          <span class="benefit-box-value">
            <T
              key-name="tasks.daily_transaction_modal.tickets"
              default-value="{count, plural, one {# ticket} other {# tickets}}"
              :params="{count: ticketBenefit}"
            />
          </span>
        </div>
      </div>
      <div class="w-full flex flex-col items-center gap-y-3">
        <div
          class="w-full sticky bottom-0 rounded-xl flex flex-col gap-y-3"
        >
          <BaseButton
            v-if="transactionDone"
            class="w-full"
            button-class="!bg-light-stone !text-black/[.67]"
            @click="$emit('hide')"
          >
            <T
              key-name="tasks.daily_reward.come_back"
              default-value="Come back tomorrow"
            />
          </BaseButton>
          <BaseButton
            v-else-if="!rawAddress"
            class="w-full scale-up-light shadow-btn"
            @click="onConnect"
          >
            <template #leftIcon>
              <img
                src="@/assets/images/gifs/money.gif"
                class="h-full w-auto max-w-[24px]"
              >
            </template>
            <T
              key-name="wallet.connect_wallet"
              default-value="Connect wallet"
            />
          </BaseButton>
          <BaseButton
            v-else
            class="w-full scale-up-light shadow-btn !px-0"
            :loading="isButtonLoading"
            @click="onTransaction"
          >
            <T
              v-if="isButtonLoading"
              key-name="lingo.giveaway.transaction.loading_cta"
              default-value="Processing..."
            />
            <div
              class="flex items-center"
              v-else
            >
              <T
                key-name="lingo.giveaway.transaction.cta"
                default-value="Complete Transaction"
              />
              <img
                src="@/assets/images/ton-symbol.png"
                class="size-5 min-w-[20px] object-cover ml-3"
              >
              <span class="ml-1.5">0.25</span>
            </div>
          </BaseButton>
        </div>
        <div class="max-w-[306px] text-center text-sm font-semibold -tracking-[0.28px] text-tropee-black">
          <T
            key-name="lingo.giveaway.transaction.explanation"
            default-value="Transaction may take a few minutes to process. You’ll be notified once it’s successful."
          />
        </div>
      </div>
    </div>
  </TheBottomSheet>

  <TransactionFailedModal
    :show="showTransactionFailedModal"
    @hide="showTransactionFailedModal = false"
  />
</template>

<style scoped lang="scss">
@import "@/assets/styles/ecommerce";

.shadow-btn {
  box-shadow: 0 0 10px -4px rgba(0, 0, 0, 0.5);
}

@media (max-height: 680px) {
  .hidden-with-small-height {
    display: none;
  }

  .extra-top-padding {
    padding-top: 48px;
  }
}
</style>
